import type { Category, MonthlyClose, Transaction, TransactionFlag } from '@eriko/shared-types';
import { formatCurrency, formatDate } from '@eriko/shared-types';
import { Badge } from '@eriko/ui/src/components/ui/badge';
import { Button } from '@eriko/ui/src/components/ui/button';
import { DataTable } from '@eriko/ui/src/components/ui/data-table';
import { Input } from '@eriko/ui/src/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@eriko/ui/src/components/ui/select';
import {
  createColumnHelper,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import {
  AlertTriangle,
  Check,
  CheckCircle,
  CheckCircle2,
  Receipt,
  TrendingDown,
  TrendingUp,
  Wallet,
} from 'lucide-react';
import { useMemo, useState } from 'react';
import { useParams } from 'react-router';
import {
  useCloseOpenItems,
  useOrgMonthlyCloses,
  useOrgTransactions,
  useUpdateMonthlyClose,
  useUpdateTransaction,
} from '../hooks/useReview';

/* ------------------------------------------------------------------ */
/*  StatCard — summary metric card with semantic accent                */
/* ------------------------------------------------------------------ */

type StatAccent = 'pos' | 'neg' | 'warn' | 'info';

const ACCENT_STYLES: Record<StatAccent, { icon: string; value: string }> = {
  pos: { icon: 'text-pos bg-pos/10', value: 'text-pos' },
  neg: { icon: 'text-neg bg-neg/10', value: 'text-neg' },
  warn: { icon: 'text-warn bg-warn/10', value: 'text-warn' },
  info: { icon: 'text-info bg-info/10', value: 'text-info' },
};

function StatCard({
  label,
  icon: Icon,
  accent,
  value,
  footer,
  hero = false,
  valueColored = false,
}: {
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  accent: StatAccent;
  value: React.ReactNode;
  footer?: React.ReactNode;
  hero?: boolean;
  valueColored?: boolean;
}) {
  const styles = ACCENT_STYLES[accent];
  return (
    <div
      className="flex-1 rounded-xl border border-surface-200 bg-surface-0 px-4 py-3 shadow-xs transition-shadow hover:shadow-sm"
    >
      <div className="flex items-center gap-2">
        <span className={`inline-flex items-center justify-center w-5 h-5 rounded-md ${styles.icon}`}>
          <Icon className="w-3 h-3" />
        </span>
        <span className="text-sm font-semibold uppercase tracking-[1.2px] text-surface-600">
          {label}
        </span>
      </div>
      <div
        className={`mt-2 font-serif font-medium tracking-tight tabular-nums leading-none ${hero ? 'text-4xl' : 'text-3xl'
          } ${valueColored ? styles.value : 'text-surface-900'}`}
      >
        {value}
      </div>
      {footer && <div className="mt-2">{footer}</div>}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Expandable detail panel                                           */
/* ------------------------------------------------------------------ */

function TransactionDetailPanel({
  transaction,
  allTransactions,
  categories,
  onClose,
  readOnly = false,
}: {
  transaction: Transaction;
  allTransactions: Transaction[];
  categories: Category[];
  onClose: () => void;
  readOnly?: boolean;
}) {
  const updateTx = useUpdateTransaction();
  const [selectedCategory, setSelectedCategory] = useState(transaction.categoryId || '');
  const [flagResolutions, setFlagResolutions] = useState<Record<string, string>>({});

  function handleCategoryChange(categoryId: string) {
    setSelectedCategory(categoryId);
    updateTx.mutate({ id: transaction.id, data: { categoryId } });
  }

  function handleMarkReviewed() {
    updateTx.mutate({ id: transaction.id, data: { isReviewed: true } });
    onClose();
  }

  function handleResolveFlag(flagId: string) {
    const resolution = flagResolutions[flagId] || 'Resolved by reviewer';
    updateTx.mutate({
      id: transaction.id,
      data: { flags: [{ id: flagId, isResolved: true, resolution }] },
    });
  }

  const isCredit = transaction.type === 'CREDIT';
  const confidencePct =
    transaction.aiConfidence != null ? Math.round(transaction.aiConfidence * 100) : null;
  const confidenceTone =
    confidencePct == null
      ? null
      : confidencePct >= 80
        ? { text: 'text-pos', bg: 'bg-pos/10' }
        : confidencePct >= 50
          ? { text: 'text-warn', bg: 'bg-warn/10' }
          : { text: 'text-neg', bg: 'bg-neg/10' };
  const aiReasoning =
    transaction.flags.find((f) => f.description)?.description ?? null;

  return (
    <div className="flex flex-col h-full overflow-y-auto">
      {/* Header */}
      <div className="px-6 pt-5 pb-4">
        <div
          className={`font-mono text-3xl font-semibold tabular-nums leading-none ${isCredit ? 'text-pos' : 'text-neg'}`}
        >
          {isCredit ? '+' : '−'}{formatCurrency(transaction.amount, 2)}
        </div>
        <div className="mt-3 text-sm font-bold uppercase tracking-[0.5px] text-surface-900">
          {transaction.description}
        </div>
        <div className="mt-1 text-xs text-surface-500">
          {formatDate(transaction.date, 'MMM d, yyyy')}
        </div>
      </div>

      {/* Actions */}
      <div className="px-6 pb-5 border-b border-surface-200">
        <div className="grid grid-cols-2 gap-2">
          <Button
            onClick={handleMarkReviewed}
            disabled={updateTx.isPending || transaction.isReviewed || readOnly}
            className="h-10 bg-pos text-white hover:bg-pos/90"
          >
            {readOnly
              ? 'Month Approved'
              : transaction.isReviewed
                ? <>Approved <Check className="ml-1" /></>
                : 'Approve'}
          </Button>
          <Button variant="outline" onClick={onClose} className="h-10">
            Close
          </Button>
        </div>
      </div>

      {/* Sections */}
      <div className="flex-1 px-6 py-5 space-y-5">
        <Section title="Category">
          <Select value={selectedCategory} onValueChange={handleCategoryChange} disabled={readOnly}>
            <SelectTrigger className="w-full h-10 text-sm justify-between">
              <SelectValue placeholder="Uncategorized" />
              {confidencePct != null && confidenceTone && (
                <span
                  className={`ml-auto mr-1 inline-flex items-center rounded px-1.5 py-0.5 font-mono text-[10px] font-semibold tabular-nums ${confidenceTone.bg} ${confidenceTone.text}`}
                >
                  {confidencePct}%
                </span>
              )}
            </SelectTrigger>
            <SelectContent>
              {categories.map((cat) => (
                <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Section>

        {transaction.flags.length > 0 && (
          <Section title="Flags">
            <div className="space-y-2">
              {transaction.flags.map((flag) => (
                <div
                  key={flag.id}
                  className="flex items-start gap-3 p-3 rounded-md border border-surface-200 bg-surface-0"
                >
                  {flag.isResolved ? (
                    <CheckCircle className="w-3.5 h-3.5 text-pos mt-0.5 shrink-0" />
                  ) : (
                    <AlertTriangle className="w-3.5 h-3.5 text-warn mt-0.5 shrink-0" />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="text-[10px]">
                        {flag.type.replace(/_/g, ' ')}
                      </Badge>
                      {flag.isResolved && (
                        <span className="text-[10px] text-pos font-medium">Resolved</span>
                      )}
                    </div>
                    <p className="mt-1 text-xs text-surface-700">{flag.description}</p>
                    <DuplicateContextBlock
                      transaction={transaction}
                      flag={flag}
                      allTransactions={allTransactions}
                    />
                    {flag.isResolved ? (
                      flag.resolution && (
                        <div className="mt-2 rounded-md border border-pos/20 bg-pos/5 px-2 py-1.5">
                          <div className="text-[9px] font-semibold uppercase tracking-[1.2px] text-pos">
                            Resolution note
                          </div>
                          <p className="mt-0.5 text-xs text-surface-700">{flag.resolution}</p>
                        </div>
                      )
                    ) : (
                      <div className="mt-2 flex items-center gap-2">
                        <Input
                          placeholder="Resolution note..."
                          value={flagResolutions[flag.id] || ''}
                          onChange={(e) =>
                            setFlagResolutions((prev) => ({ ...prev, [flag.id]: e.target.value }))
                          }
                          disabled={readOnly}
                          className="h-7 text-xs flex-1"
                        />
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleResolveFlag(flag.id)}
                          disabled={updateTx.isPending || readOnly}
                        >
                          Resolve
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </Section>
        )}

        <Section title="Statement source">
          <p className="text-sm text-surface-900">
            {transaction.rawDescription ?? transaction.description}
          </p>
        </Section>

        {aiReasoning && (
          <Section title="AI reasoning">
            <p className="text-sm text-surface-900">{aiReasoning}</p>
          </Section>
        )}

        {/*<Section title="Reviewer note">
          <textarea
            placeholder="Add a note..."
            disabled={readOnly}
            className="w-full min-h-[72px] resize-y rounded-md border border-surface-200 bg-surface-0 px-3 py-2 text-sm text-surface-900 placeholder:text-surface-400 focus:outline-none focus:ring-2 focus:ring-surface-300 disabled:opacity-50"
          />
        </Section>*/}
      </div>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="text-[10px] font-semibold uppercase tracking-[1.4px] text-surface-400">
        {title}
      </div>
      <div className="mt-2">{children}</div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Columns                                                            */
/* ------------------------------------------------------------------ */

const col = createColumnHelper<Transaction>();

const columns = [
  col.accessor('date', {
    header: 'Date',
    size: 70,
    cell: (info) => (
      <span className="font-mono text-[11px] font-medium text-surface-800 tabular-nums">
        {formatDate(info.getValue(), 'MMM d')}
      </span>
    ),
  }),
  col.accessor('description', {
    header: 'Description',
    cell: (info) => {
      const flags = info.row.original.flags.filter((f) => !f.isResolved).length;
      return (
        <div className="flex items-center gap-2">
          {flags > 0 && (
            <span
              className="inline-flex items-center justify-center w-1.5 h-1.5 rounded-full bg-neg shrink-0 ring-2 ring-neg/20"
              aria-label={`${flags} unresolved flag${flags === 1 ? '' : 's'}`}
            />
          )}
          <span className="font-medium text-surface-900 truncate max-w-60">
            {info.getValue()}
          </span>
        </div>
      );
    },
  }),
  col.accessor('amount', {
    header: () => <span className="text-right block">Amount</span>,
    size: 100,
    cell: (info) => {
      const tx = info.row.original;
      const isCredit = tx.type === 'CREDIT';
      return (
        <span
          className={`block text-right font-mono text-[11px] font-semibold tabular-nums ${isCredit ? 'text-pos' : 'text-neg'
            }`}
        >
          {isCredit ? '+' : '−'}{formatCurrency(info.getValue(), 2)}
        </span>
      );
    },
  }),
  col.accessor('category', {
    header: 'Category',
    cell: (info) => {
      const cat = info.getValue();
      return cat ? (
        <span className="inline-flex items-center rounded-md bg-surface-100 px-1.5 py-0.5 text-[11px] font-medium text-surface-800 border border-surface-200">
          {cat.name}
        </span>
      ) : (
        <span className="text-surface-400 italic">Uncategorized</span>
      );
    },
  }),
  col.accessor('aiConfidence', {
    header: () => <span className="text-right block">Confidence</span>,
    size: 90,
    cell: (info) => {
      const val = info.getValue();
      if (val == null) {
        return <span className="block text-right text-surface-400">—</span>;
      }
      const pct = Math.round(val * 100);
      const tone =
        val >= 0.8
          ? { text: 'text-pos', bar: 'bg-pos', track: 'bg-pos/15' }
          : val >= 0.5
            ? { text: 'text-warn', bar: 'bg-warn', track: 'bg-warn/15' }
            : { text: 'text-neg', bar: 'bg-neg', track: 'bg-neg/15' };
      return (
        <div className="flex items-center justify-end gap-1.5">
          <div className={`h-1 w-10 rounded-full overflow-hidden ${tone.track}`}>
            <div
              className={`h-full ${tone.bar} rounded-full`}
              style={{ width: `${pct}%` }}
            />
          </div>
          <span className={`font-mono text-[11px] font-semibold tabular-nums w-8 text-right ${tone.text}`}>
            {pct}%
          </span>
        </div>
      );
    },
  }),
  col.display({
    id: 'status',
    header: 'Status',
    size: 96,
    cell: ({ row }) => {
      const tx = row.original;
      const flagged = tx.flags.some((f) => !f.isResolved);
      if (tx.isReviewed) {
        return (
          <span className="inline-flex items-center gap-1.5 rounded-full bg-pos/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-pos">
            <span className="w-1.5 h-1.5 rounded-full bg-pos" />
            Reviewed
          </span>
        );
      }
      if (flagged) {
        return (
          <span className="inline-flex items-center gap-1.5 rounded-full bg-neg/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-neg">
            <span className="w-1.5 h-1.5 rounded-full bg-neg" />
            Flagged
          </span>
        );
      }
      return (
        <span className="inline-flex items-center gap-1.5 rounded-full bg-surface-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-surface-600 border border-surface-200">
          <span className="w-1.5 h-1.5 rounded-full bg-surface-400" />
          Pending
        </span>
      );
    },
  }),
];

/* ------------------------------------------------------------------ */
/*  Page                                                               */
/* ------------------------------------------------------------------ */

export function MonthlyCloseReviewPage() {
  const { orgId, closeId } = useParams<{ orgId: string; closeId: string }>();

  const { data: txData, isLoading: txLoading } = useOrgTransactions(orgId!, closeId);
  const { data: closes } = useOrgMonthlyCloses(orgId!);
  const { data: openItems } = useCloseOpenItems(closeId);
  const updateClose = useUpdateMonthlyClose();

  const close = closes?.find((c: MonthlyClose) => c.id === closeId);
  const isClosed = close?.status === 'APPROVED' || close?.status === 'DELIVERED';
  const openItemCount =
    (openItems?.unresolvedFlags ?? 0) + (openItems?.openBlockingConversations ?? 0);
  const transactions = useMemo(() => txData?.transactions ?? [], [txData]);
  const categories = useMemo(() => txData?.categories ?? [], [txData]);

  const [selectedTxId, setSelectedTxId] = useState<string | null>(null);
  const selectedTx = useMemo(
    () => transactions.find((t: Transaction) => t.id === selectedTxId) ?? null,
    [transactions, selectedTxId],
  );

  // TanStack Table's useReactTable returns memoizable values that React Compiler
  // flags as "incompatible-library" — known false positive. Suppression is
  // per-callsite; safe to remove once the rule recognizes the library.
  // eslint-disable-next-line react-hooks/incompatible-library
  const table = useReactTable({
    data: transactions,
    columns,
    getRowId: (row) => row.id,
    getCoreRowModel: getCoreRowModel(),
  });

  function handleSignOff(force = false) {
    if (!closeId) return;
    updateClose.mutate({ id: closeId, data: { status: 'APPROVED', force } });
  }

  const reviewedCount = transactions.filter((t: Transaction) => t.isReviewed).length;
  const flaggedCount = transactions.reduce(
    (acc: number, t: Transaction) => acc + t.flags.filter((f) => !f.isResolved).length,
    0,
  );

  return (
    <div className="flex flex-1 overflow-hidden">
      <div className="flex flex-col flex-1 overflow-hidden">
      {/* Header */}
      <div className="flex items-start justify-between px-6 pt-5 shrink-0">
        <div>
          <h1 className="text-lg font-bold text-surface-900">
            Monthly Close Review
          </h1>
          {close && (
            <div className="text-xs text-surface-500 mt-0.5 flex items-center gap-1.5">
              <span>
                {new Date(close.year, close.month - 1).toLocaleDateString('en-US', {
                  month: 'long',
                  year: 'numeric',
                })}
                {' \u00B7'}
              </span>
              <Badge
                variant={close.status === 'APPROVED' || close.status === 'DELIVERED' ? 'success' : 'info'}
              >
                {close.status.replace(/_/g, ' ')}
              </Badge>
            </div>
          )}
        </div>
        <div className="flex flex-col items-end gap-1">
          {!isClosed && openItemCount > 0 && (
            <span
              className="inline-flex items-center gap-1 rounded-full bg-warn/10 px-2 py-0.5 text-[11px] font-semibold text-warn"
              title={`${openItems?.unresolvedFlags ?? 0} unresolved flag(s) · ${openItems?.openBlockingConversations ?? 0} open client question(s) — these block sign-off`}
            >
              <AlertTriangle className="w-3 h-3" />
              {openItemCount} open item{openItemCount === 1 ? '' : 's'}
            </span>
          )}
          <Button
            onClick={() => handleSignOff()}
            variant='default'
            disabled={updateClose.isPending || isClosed}
          >
            {isClosed ? 'Already Approved' : 'Sign Off Month'}
          </Button>
          {updateClose.isError && (
            <div className="text-right max-w-xs">
              <p className="text-xs text-red-600">
                {(updateClose.error as { message?: string } | null)?.message ??
                  'Failed to sign off. Please try again.'}
              </p>
              {(updateClose.error as { code?: string } | null)?.code === 'CLOSE_HAS_OPEN_ITEMS' && (
                <button
                  type="button"
                  onClick={() => handleSignOff(true)}
                  disabled={updateClose.isPending}
                  className="text-xs font-medium text-red-700 underline mt-0.5"
                >
                  Sign off anyway
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Summary cards */}
      {close && (
        <div className="flex gap-2 px-6 pt-4 shrink-0">
          <StatCard
            label="Revenue"
            icon={TrendingUp}
            accent="pos"
            value={
              close.totalRevenue != null
                ? formatCurrency(close.totalRevenue, 0, close.currency ?? undefined)
                : '—'
            }
          />
          <StatCard
            label="Expenses"
            icon={TrendingDown}
            accent="warn"
            value={
              close.totalExpenses != null
                ? formatCurrency(close.totalExpenses, 0, close.currency ?? undefined)
                : '—'
            }
          />
          <StatCard
            label="Net Profit"
            icon={Wallet}
            accent={close.netProfit != null && close.netProfit < 0 ? 'neg' : 'pos'}
            value={
              close.netProfit != null
                ? formatCurrency(close.netProfit, 0, close.currency ?? undefined)
                : '—'
            }
            hero
            valueColored
          />
          <StatCard
            label="Transactions"
            icon={Receipt}
            accent="info"
            value={transactions.length}
          />
          <StatCard
            label="Progress"
            icon={CheckCircle2}
            accent="info"
            hero
            value={
              <span>
                {reviewedCount}
                <span className="text-xl text-surface-400">
                  /{transactions.length}
                </span>
              </span>
            }
            footer={
              <div className="space-y-1.5">
                <div className="h-1 w-full rounded-full bg-info/15 overflow-hidden">
                  <div
                    className="h-full bg-info rounded-full transition-[width] duration-300"
                    style={{
                      width: `${transactions.length ? (reviewedCount / transactions.length) * 100 : 0}%`,
                    }}
                  />
                </div>
                {flaggedCount > 0 ? (
                  <span className="text-[11px] font-semibold text-neg">
                    {flaggedCount} flag{flaggedCount === 1 ? '' : 's'}
                  </span>
                ) : (
                  <span className="text-[11px] text-surface-500">No flags</span>
                )}
              </div>
            }
          />
        </div>
      )}

      {/* Transactions table */}
      <div className="flex-1 overflow-auto px-6 pt-4 pb-6">
        {txLoading ? (
          <div className="flex items-center justify-center py-16">
            <div className="h-6 w-6 animate-spin rounded-full border-2 border-surface-900 border-t-transparent" />
          </div>
        ) : (
          <DataTable
            table={table}
            onRowClick={(tx) => setSelectedTxId(tx.id)}
            emptyMessage="No transactions for this period."
          />
        )}
      </div>
      </div>
      {selectedTx && (
        <aside className="w-[380px] shrink-0 border-l border-surface-200 bg-surface-0">
          <TransactionDetailPanel
            transaction={selectedTx}
            allTransactions={transactions}
            categories={categories}
            onClose={() => setSelectedTxId(null)}
            readOnly={isClosed}
          />
        </aside>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  DuplicateContextBlock — shows both rows side-by-side for DUPLICATE */
/* ------------------------------------------------------------------ */

function DuplicateContextBlock({
  transaction,
  flag,
  allTransactions,
}: {
  transaction: Transaction;
  flag: TransactionFlag;
  allTransactions: Transaction[];
}) {
  if (flag.type !== 'DUPLICATE') return null;
  if (!flag.metadata || typeof flag.metadata !== 'object') return null;
  if (!('otherTransactionId' in flag.metadata)) return null;

  const otherId = flag.metadata.otherTransactionId as string;
  const other = allTransactions.find((t) => t.id === otherId);
  if (!other) return null;

  const fmtTime = (iso: string) => {
    const d = new Date(iso);
    const h = d.getHours();
    const m = d.getMinutes();
    return h === 0 && m === 0
      ? '—'
      : `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
  };

  const fmtBalance = (b: number | null) =>
    b === null ? '—' : `$${b.toFixed(2)}`;

  return (
    <div className="mt-2 rounded-md border border-surface-200 bg-surface-50 p-2 font-mono text-[11px] text-surface-700 space-y-1">
      <div>
        <span className="text-surface-400">This row:</span>{' '}
        {fmtTime(transaction.date)} ·{' '}
        {transaction.rawDescription ?? transaction.description} · balance after:{' '}
        {fmtBalance(transaction.balance)}
      </div>
      <div>
        <span className="text-surface-400">Other row:</span>{' '}
        {fmtTime(other.date)} ·{' '}
        {other.rawDescription ?? other.description} · balance after:{' '}
        {fmtBalance(other.balance)}
      </div>
    </div>
  );
}
